import React, { useMemo, useState } from "react";
import { REVIEW_GATE_STATUS, getExecutiveSummaryQaStatusLabel } from "./executiveSummaryQaStatus";

const SEGMENT_ORDER = [
    REVIEW_GATE_STATUS.QA_PASSED,
    REVIEW_GATE_STATUS.READY_FOR_QA,
    REVIEW_GATE_STATUS.IN_PROGRESS
];

const COUNT_KEY = {
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "in_progress",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "ready_for_qa",
    [REVIEW_GATE_STATUS.QA_PASSED]: "qa_passed"
};

const SEGMENT_BAR_CLASS = {
    [REVIEW_GATE_STATUS.QA_PASSED]: "exec-sum-qa-progress-card__segment--passed",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "exec-sum-qa-progress-card__segment--ready",
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "exec-sum-qa-progress-card__segment--in-progress"
};

const LEGEND_DOT_CLASS = {
    [REVIEW_GATE_STATUS.QA_PASSED]: "exec-sum-qa-progress-card__dot--passed",
    [REVIEW_GATE_STATUS.READY_FOR_QA]: "exec-sum-qa-progress-card__dot--ready",
    [REVIEW_GATE_STATUS.IN_PROGRESS]: "exec-sum-qa-progress-card__dot--in-progress"
};

function formatBandCount(count) {
    return `${count} band${count === 1 ? "" : "s"}`;
}

function formatStatusPercentage(count, totalCount) {
    if (!totalCount) return "0%";
    return `${Math.round((count / totalCount) * 100)}%`;
}

function ExecutiveSummaryQaProgressCard({ breakdown }) {
    const [expanded, setExpanded] = useState(false);

    const { totalCount = 0, counts } = breakdown || {};
    const readyCount = counts?.ready_for_qa ?? 0;
    const passedCount = counts?.qa_passed ?? 0;

    const statusItems = useMemo(
        () =>
            SEGMENT_ORDER.map(status => ({
                status,
                count: counts?.[COUNT_KEY[status]] ?? 0,
                label: getExecutiveSummaryQaStatusLabel(status)
            })),
        [counts]
    );

    if (!totalCount) return null;

    const segments = statusItems.filter(({ count }) => count > 0);

    const toggleExpanded = () => setExpanded(prev => !prev);

    return (
        <div className="exec-sum-qa-progress-card">
            <button
                type="button"
                className="exec-sum-qa-progress-card__header"
                onClick={toggleExpanded}
                aria-expanded={expanded}
                aria-controls="exec-sum-qa-progress-card-legend"
            >
                <span className="exec-sum-qa-progress-card__title">Bands</span>
                <span className="exec-sum-qa-progress-card__summary" aria-live="polite">
                    <span className="exec-sum-qa-progress-card__summary-stat exec-sum-qa-progress-card__summary-stat--passed">
                        Passed: {passedCount}/{totalCount}
                    </span>
                    <span className="exec-sum-qa-progress-card__summary-stat exec-sum-qa-progress-card__summary-stat--ready">
                        Ready: {readyCount}/{totalCount}
                    </span>
                    <i
                        className={`fas fa-chevron-${expanded ? "up" : "down"} exec-sum-qa-progress-card__chevron`}
                        aria-hidden="true"
                    />
                </span>
            </button>
            <div className="exec-sum-qa-progress-card__bar" role="presentation" aria-hidden="true">
                {segments.map(({ status, count }) => (
                    <div
                        key={status}
                        className={`exec-sum-qa-progress-card__segment ${SEGMENT_BAR_CLASS[status] || ""}`}
                        style={{ width: `${(count / totalCount) * 100}%` }}
                    />
                ))}
            </div>
            {expanded ? (
                <div
                    id="exec-sum-qa-progress-card-legend"
                    className="exec-sum-qa-progress-card__legend"
                    role="list"
                >
                    {statusItems.map(({ status, count, label }) => (
                        <div key={status} className="exec-sum-qa-progress-card__legend-item" role="listitem">
                            <span
                                className={`exec-sum-qa-progress-card__dot ${LEGEND_DOT_CLASS[status] || ""}`}
                                aria-hidden="true"
                            />
                            <span className="exec-sum-qa-progress-card__legend-label">{label}</span>
                            <span className="exec-sum-qa-progress-card__legend-value">
                                <span className="exec-sum-qa-progress-card__legend-count">
                                    {formatBandCount(count)}
                                </span>
                                <span className="exec-sum-qa-progress-card__legend-percentage">
                                    {formatStatusPercentage(count, totalCount)}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export default ExecutiveSummaryQaProgressCard;
